import { isMobile } from '../../utils/check-media'
import { debounce } from '../../utils/debounce'
import { BaseComponent } from '../../components/base-component'
import { CarouselComponent } from 'src/scripts/components/carousel'
import { ModalComponent } from 'src/scripts/components/modal'
import { $el, $list, $elParent } from 'src/scripts/utils/utils'

const PIN_SELECTOR = '[data-lookbook-pin]'
const BLOCK_SELECTOR = '[data-lookbook-block]'
const MODAL_SELECTOR = '[data-lookbook-modal]'
const CAROUSEL_SELECTOR = '[data-lookbook-carousel]'
const DESKTOP_CONTENT_SELECTOR = '[data-lookbook-desktop-content]'
const MOBILE_CONTENT_SELECTOR = '[data-lookbook-mobile-content]'

export class LookbookComponent extends BaseComponent {
  animationPausedAttribute: string = 'animation-paused'
  notHighlightedAttribute: string = 'not-highlighted'
  pins: HTMLElement[]
  highlightedBlockId: string
  resizeObserver: ResizeObserver
  carousels: CarouselComponent[]

  mountComponent(): void {
    const pins = $list(PIN_SELECTOR, this)

    pins.forEach(pin => {
      this.addListener(pin, 'mouseenter', this.handlePinEnter)
      this.addListener(pin, 'mouseleave', this.handlePinLeave)
      this.addListener(pin, 'click', this.handlePinClick)
    })

    this.carousels = $list(CAROUSEL_SELECTOR, this)

    this.carousels.forEach(carousel => {
      carousel.embla.on('select', this.handleCarouselSelect)
    })

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)
  }

  unmountComponent(): void {
    this.resizeObserver.disconnect()

    this.carousels.forEach(carousel => {
      carousel.embla.off('select', this.handleCarouselSelect)
    })
  }

  handlePinEnter = (): void => {
    this.setAnimationPausedState(true)
  }

  handlePinLeave = (): void => {
    if (this.highlightedBlockId) {
      return
    }

    this.setAnimationPausedState(false)
  }

  handleResize = debounce(() => {
    this.showBlockById(this.highlightedBlockId)
  }, 500)

  handlePinClick = (event: MouseEvent): void => {
    const pin = event.target as HTMLElement
    const blockId = pin.dataset.blockId

    this.showBlockById(blockId)
    this.addListener(window, 'click', this.handleOutsideClick)
  }

  handleOutsideClick = (event: MouseEvent): void => {
    const pin = $elParent(PIN_SELECTOR, event.target)

    if (!pin) {
      this.showBlockById()
      this.removeListener(window, 'click', this.handleOutsideClick)
    }
  }

  showBlockById(blockId?: string): void {
    this.updateSelectedStateForPins(blockId)

    if (blockId) {
      this.setHighlightBlockById(blockId)
      this.setAnimationPausedState(true)
    } else {
      this.resetHighlight()
      this.setAnimationPausedState(false)
    }

    this.highlightedBlockId = blockId
  }

  updateSelectedStateForPins(blockId?: string) {
    const pins = $list(PIN_SELECTOR, this)
    pins.forEach(pin => {
      pin.toggleAttribute('is-selected', pin.dataset.blockId === blockId)
    })
  }

  setAnimationPausedState(isAnimationPaused: boolean): void {
    this.toggleAttribute(this.animationPausedAttribute, isAnimationPaused)
  }

  setHighlightBlockById(blockId?: string): void {
    this.blocks.forEach((block, index) => {
      const isHighlighted = block.dataset.blockId === blockId

      if (isHighlighted) {
        this.scrollToProductByIndex(index)
      }

      block.toggleAttribute(this.notHighlightedAttribute, !isHighlighted)
    })
  }

  scrollToProductByIndex(index: number) {
    const block = this.blocks[index]
    const carousel: CarouselComponent = $elParent(CAROUSEL_SELECTOR, block)

    if (carousel) {
      carousel.embla.scrollTo(index)
    } else {
      block.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  handleCarouselSelect = () => {
    if (this.highlightedBlockId && isMobile()) {
      this.resetHighlight()
      this.setAnimationPausedState(false)
      this.updateSelectedStateForPins()

      this.highlightedBlockId = ''
    }
  }

  resetHighlight() {
    this.blocks.forEach(block => {
      block.removeAttribute(this.notHighlightedAttribute)
    })
  }

  get blocks(): HTMLElement[] {
    const desktopContent = $el(DESKTOP_CONTENT_SELECTOR, this)
    const mobileContent = $el(MOBILE_CONTENT_SELECTOR, this)
    const content = isMobile() ? mobileContent : desktopContent
    const modal: ModalComponent = $el(MODAL_SELECTOR, content)
    const node = modal ? modal.element : content

    return $list(BLOCK_SELECTOR, node)
  }
}
